// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@flip/XDL/types";
import color from "@flip/XDL/Utilities/color";
import { getSystemColor } from "@flip/XDL/Utilities/color";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

/**
 * Loading style: show 3 dots with animation
 * <Button>
 *   <StyledWrapper {...loadingButtonStyle} />
 * </Button>
 */
export const loadingButtonStyle = (loadingColor = "common.ui8") => {
    return {
        css: css`
            position: relative;
            left: -9999px;
            width: 3px;
            height: 3px;
            border-radius: 5px;
            background-color: ${color(loadingColor)};
            color: ${color(loadingColor)};
            box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
            animation: dotPulse 1.5s infinite linear;
            animation-delay: 0.25s;

            &::before,
            &::after {
                content: "";
                display: inline-block;
                position: absolute;
                top: 0;
                width: 3px;
                height: 3px;
                border-radius: 5px;
                background-color: ${color(loadingColor)};
                color: ${color(loadingColor)};
            }

            &::before {
                box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                animation: dotPulseBefore 1.5s infinite linear;
                animation-delay: 0s;
            }

            &::after {
                box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                animation: dotPulseAfter 1.5s infinite linear;
                animation-delay: 0.5s;
            }

            @keyframes dotPulseBefore {
                0% {
                    box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 9984px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                }
            }

            @keyframes dotPulse {
                0% {
                    box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 9999px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
                }
            }

            @keyframes dotPulseAfter {
                0% {
                    box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 10014px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                }
            }
        `,
    };
};

export const buttonColors: {
    [key: string]: { main: string; hover: string; disabled: string; text: string; focus: string };
} = {
    primary: {
        main: "secondary",
        hover: "common.ui13",
        disabled: "secondary",
        text: "common.ui8",
        focus: "common.ui12",
    },
    secondary: {
        main: "text.link",
        hover: "common.ui13",
        disabled: "primary",
        text: "text.link",
        focus: "common.ui12",
    },
};

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        mergeCss: true,
        shape: "rounded",
        typographyProps: {
            color: buttonColors.primary.text,
        },
        hoverStyle: {
            background: `${getSystemColor(buttonColors.primary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
            color: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        css: css`
            &:focus {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
                ${TypographyStyle} {
                    color: ${getSystemColor("common.white")};
                }
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.disabled, 0.33)};
                border-color: transparent;

                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.primary.text)};
                }
            }
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        shape: "rounded",
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.secondary.hover)}`,
            color: `${getSystemColor(buttonColors.secondary.hover)}`,
        },
        css: css`
            border: 1px solid;
            &:focus {
                border-color: ${getSystemColor(buttonColors.secondary.focus)};
                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.secondary.focus)};
                }
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.secondary.disabled, 0.33)};
                ${TypographyStyle} {
                    color: ${getSystemColor(buttonColors.secondary.disabled, 0.33)};
                }
            }
        `,
    },
} as xBaseTheme["button"];
